import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Form, 
  Input, 
  Button, 
  Select, 
  Slider, 
  Card, 
  message, 
  Spin,
  Row,
  Col,
  Divider
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { emotionApi, emotionRecordApi } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const CreateEmotionRecordPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [form] = Form.useForm();
  
  const [loading, setLoading] = useState(false);
  const [emotions, setEmotions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [generatingRandom, setGeneratingRandom] = useState(false);

  useEffect(() => {
    const fetchEmotions = async () => {
      setLoading(true);
      try {
        const response = await emotionApi.getAllEmotions();
        setEmotions(response.data);
      } catch (error) {
        console.error('获取情绪数据失败:', error);
        message.error('获取情绪类型失败');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEmotions();
  }, []);

  // 选择情绪类型时更新选中的情绪
  const handleEmotionChange = (emotionId) => {
    const selectedEmotion = emotions.find(emotion => emotion.id === emotionId);
    setSelectedEmotion(selectedEmotion);
    
    // 设置强度默认值
    if (selectedEmotion) {
      form.setFieldsValue({ intensity: selectedEmotion.intensity });
    }
  };

  // 提交表单
  const onFinish = async (values) => {
    setSubmitting(true);
    
    try {
      // 创建情绪记录数据对象
      const recordData = {
        userId: currentUser.id.toString(), // 确保userId是字符串
        emotionId: values.emotionId.toString(), // 确保emotionId是字符串
        description: values.event, // 将event映射为description
        intensity: values.intensity,
        recordTime: new Date().toISOString(), // 使用ISO格式的UTC时间
        // 可以将其他字段作为tags数组或合并到description中
        tags: [
          values.physicalResponse && `身体反应: ${values.physicalResponse}`,
          values.thoughtsAndBeliefs && `想法: ${values.thoughtsAndBeliefs}`,
          values.behaviors && `行为: ${values.behaviors}`
        ].filter(Boolean) // 过滤掉undefined/空值
      };
      
      console.log("提交的记录数据:", recordData);
      
      // 确保Content-Type设置正确
      const response = await emotionRecordApi.createEmotionRecord(recordData);
      
      console.log("API响应:", response.data);
      
      message.success('情绪记录创建成功');
      
      // 导航到详情页或列表页
      navigate(`/emotion-records/${response.data.id}`);
    } catch (error) {
      console.error('创建情绪记录失败:', error.response?.data || error.message);
      message.error('创建情绪记录失败: ' + (error.response?.data?.message || error.message || '请稍后再试'));
    } finally {
      setSubmitting(false);
    }
  };

  // 获取情绪强度的描述文本
  const getIntensityDescription = (intensity) => {
    if (intensity <= 3) return '轻微';
    if (intensity <= 5) return '中等';
    if (intensity <= 7) return '较强';
    return '强烈';
  };

  // 根据情绪强度获取颜色
  const getIntensityColor = (intensity) => {
    if (intensity <= 3) return '#52c41a';
    if (intensity <= 5) return '#1890ff';
    if (intensity <= 7) return '#faad14';
    return '#f5222d';
  };

  // 随机生成情绪记录
  const generateRandomEmotionRecord = async () => {
    if (emotions.length === 0) {
      message.error('请先等待情绪数据加载完成');
      return;
    }

    setGeneratingRandom(true);
    
    try {
      // 随机选择情绪
      const randomEmotion = emotions[Math.floor(Math.random() * emotions.length)];
      
      // 随机生成强度 (1-10)
      const randomIntensity = Math.floor(Math.random() * 10) + 1;
      
      // 随机生成事件描述
      const eventDescriptions = [
        '工作中遇到了挑战性任务，需要解决复杂问题',
        '与朋友聚会，分享了很多有趣的话题',
        '看到一部感人的电影，情节触动了内心',
        '收到了意外的好消息，心情很愉快',
        '在学习新技能时遇到困难，有点沮丧',
        '天气很好，在公园里散步放松心情',
        '完成了一个重要项目，感觉很有成就感',
        '与家人通话，听到了温暖的关怀',
        '阅读了一本启发性的书籍，有很多感悟',
        '参加了运动锻炼，释放了压力'
      ];
      
      const physicalResponses = [
        '心跳加速，感觉血液循环加快',
        '呼吸变得深沉，肩膀放松下来',
        '感觉胸口有些紧张，手心出汗',
        '全身充满活力，精神状态很好',
        '肌肉有些紧张，需要深呼吸放松',
        '感觉温暖舒适，身体很放松'
      ];
      
      const thoughts = [
        '觉得自己能够应对这个挑战',
        '对未来感到乐观和期待',
        '需要找到更好的解决方案',
        '感谢生活中的美好时刻',
        '希望能够更好地管理情绪',
        '珍惜当下的感受和体验'
      ];
      
      const behaviors = [
        '主动与他人分享感受',
        '选择听音乐来调节心情',
        '写日记记录当时的想法',
        '去户外走走，接触自然',
        '做一些喜欢的小事情',
        '联系朋友聊天交流'
      ];

      const randomEvent = eventDescriptions[Math.floor(Math.random() * eventDescriptions.length)];
      const randomPhysical = physicalResponses[Math.floor(Math.random() * physicalResponses.length)];
      const randomThought = thoughts[Math.floor(Math.random() * thoughts.length)];
      const randomBehavior = behaviors[Math.floor(Math.random() * behaviors.length)];

      // 创建随机情绪记录
      const recordData = {
        userId: currentUser.id.toString(),
        emotionId: randomEmotion.id.toString(),
        description: randomEvent,
        intensity: randomIntensity,
        recordTime: new Date().toISOString(),
        tags: [
          `身体反应: ${randomPhysical}`,
          `想法: ${randomThought}`,
          `行为: ${randomBehavior}`
        ]
      };

      console.log("生成的随机记录数据:", recordData);
      
      const response = await emotionRecordApi.createEmotionRecord(recordData);
      
      message.success(`成功生成随机情绪记录: ${randomEmotion.name} (强度: ${randomIntensity})`);
      
      // 可以选择是否导航到详情页
      // navigate(`/emotion-records/${response.data.id}`);
      
    } catch (error) {
      console.error('生成随机情绪记录失败:', error.response?.data || error.message);
      message.error('生成随机情绪记录失败: ' + (error.response?.data?.message || error.message || '请稍后再试'));
    } finally {
      setGeneratingRandom(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '20px' }}>加载情绪数据中...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <Title level={2}>记录新情绪</Title>
          <Paragraph style={{ marginBottom: '0' }}>
            记录您的情绪体验，包括触发事件、身体反应、想法和行为。这将帮助您更好地了解自己的情绪模式。
          </Paragraph>
        </div>
        <Button 
          type="primary" 
          ghost
          size="large"
          loading={generatingRandom}
          onClick={generateRandomEmotionRecord}
          style={{ 
            minWidth: '140px', 
            height: '48px',
            borderColor: '#1890ff',
            color: '#1890ff'
          }}
        >
          🎲 随机生成
        </Button>
      </div>
      
      <Card style={{ marginBottom: '24px' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            intensity: 5
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="emotionId"
                label="选择情绪类型"
                rules={[{ required: true, message: '请选择情绪类型' }]}
              >
                <Select 
                  placeholder="请选择您感受到的情绪" 
                  onChange={handleEmotionChange}
                  size="large"
                >
                  {emotions.map(emotion => (
                    <Option key={emotion.id} value={emotion.id}>
                      {emotion.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col xs={24} md={12}>
              <Form.Item
                name="intensity"
                label={
                  <span>
                    情绪强度 
                    {form.getFieldValue('intensity') && (
                      <Text type="secondary" style={{ marginLeft: '8px' }}>
                        ({getIntensityDescription(form.getFieldValue('intensity'))})
                      </Text>
                    )}
                  </span>
                }
                rules={[{ required: true, message: '请设置情绪强度' }]}
              >
                <Slider
                  min={1}
                  max={10}
                  marks={{
                    1: '1',
                    3: '3',
                    5: '5',
                    7: '7',
                    10: '10'
                  }}
                  tipFormatter={(value) => `${value} - ${getIntensityDescription(value)}`}
                  trackStyle={{ backgroundColor: getIntensityColor(form.getFieldValue('intensity') || 5) }}
                  handleStyle={{ borderColor: getIntensityColor(form.getFieldValue('intensity') || 5) }}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Divider />
          
          <Form.Item
            name="event"
            label="触发事件"
            rules={[{ required: true, message: '请描述引发这种情绪的事件' }]}
          >
            <TextArea 
              placeholder="描述引发这种情绪的事件或情景" 
              autoSize={{ minRows: 3, maxRows: 6 }}
            />
          </Form.Item>
          
          <Form.Item
            name="physicalResponse"
            label="身体反应"
          >
            <TextArea 
              placeholder="描述您的身体反应，如心跳加速、呼吸变化、肌肉紧张等" 
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
          
          <Form.Item
            name="thoughtsAndBeliefs"
            label="想法和信念"
          >
            <TextArea 
              placeholder="记录这种情绪引发的想法和信念" 
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
          
          <Form.Item
            name="behaviors"
            label="行为反应"
          >
            <TextArea 
              placeholder="描述这种情绪导致的行为反应" 
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
          
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={() => navigate('/emotion-records')}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                保存记录
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
      
      {selectedEmotion && (
        <Card title={`关于${selectedEmotion.name}的信息`}>
          <Paragraph>
            <strong>描述：</strong> {selectedEmotion.description || '暂无描述'}
          </Paragraph>
          <Paragraph>
            <strong>强度：</strong> {selectedEmotion.intensity} / 10
          </Paragraph>
          <Paragraph>
            <strong>常见触发因素：</strong> {selectedEmotion.commonTriggers || '暂无相关信息'}
          </Paragraph>
          <Paragraph>
            <strong>常见应对策略：</strong> {selectedEmotion.copingStrategies || '暂无相关信息'}
          </Paragraph>
        </Card>
      )}
    </div>
  );
};

export default CreateEmotionRecordPage; 